import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

/**
 * «Sertifiseringer» block on the user's profile.
 *
 * Earned badges come first (from progress rows with completedDate, even when
 * the course is no longer listed on this domain), then the courses available
 * here that are in progress or not started, greyed and linking to the course.
 * Renders nothing when there is nothing to show.
 */
export function ElearningBadgesView({ courses = [], progressRows = [] }) {
    const byCourse = {};
    progressRows.forEach((p) => { byCourse[p.course] = p; });
    const courseById = {};
    courses.forEach((c) => { courseById[c.id] = c; });

    const earned = progressRows
        .filter(p => p.completedDate)
        .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))
        .map(p => ({ progress: p, course: courseById[p.course] || null }));

    const pending = courses
        .filter(c => !(byCourse[c.id] && byCourse[c.id].completedDate))
        .map(c => ({ course: c, progress: byCourse[c.id] || null }));

    if (earned.length === 0 && pending.length === 0) {
        return null;
    }

    const badgeImage = course => (Array.isArray(course && course.badgeImages) && course.badgeImages[0] && course.badgeImages[0].s3MediumLink) || null;

    return (
        <div class='mt-4'>
            <h5 class='fw-semibold mb-3'>
                <i class='fa-solid fa-award me-2' /><Text id='elearning.certifications'>Certifications</Text>
            </h5>
            <div class='d-flex flex-wrap gap-3'>
                {earned.map(({ progress, course }) => {
                    const img = badgeImage(course);
                    const title = progress.badgeTitle || (course && (course.badgeTitle || course.title)) || '';
                    const inner = (
                        <>
                            <div class='rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 text-bg-warning' style='width: 64px; height: 64px; overflow: hidden;'>
                                {img ? <img src={img} alt={title} style='width: 64px; height: 64px; object-fit: cover;' /> : <i class='fa-solid fa-award fs-3' />}
                            </div>
                            <div class='fw-semibold small'>{title}</div>
                            <div class='small text-body-secondary'>
                                <Text id='elearning.earned'>Earned</Text> {util.formatDate(progress.completedDate)}
                            </div>
                        </>
                    );
                    return course ? (
                        <a key={`e-${progress.course}`} href={`/elearning/${course.id}`} class='card text-center text-decoration-none text-body rounded-4 border-0 shadow-sm p-3' style='width: 150px;'>
                            {inner}
                        </a>
                    ) : (
                        <div key={`e-${progress.course}`} class='card text-center rounded-4 border-0 shadow-sm p-3' style='width: 150px;'>
                            {inner}
                        </div>
                    );
                })}
                {pending.map(({ course, progress }) => {
                    const img = badgeImage(course);
                    const done = progress && Array.isArray(progress.lessonsDone) ? progress.lessonsDone.length : 0;
                    const total = Number(course.lessonCount) || 0;
                    return (
                        <a key={`p-${course.id}`} href={`/elearning/${course.id}`} class='card text-center text-decoration-none text-body rounded-4 border-0 shadow-sm p-3 opacity-50' style='width: 150px;'>
                            <div class='rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 bg-body-secondary' style='width: 64px; height: 64px; overflow: hidden; filter: grayscale(1);'>
                                {img ? <img src={img} alt={course.badgeTitle || course.title} style='width: 64px; height: 64px; object-fit: cover;' /> : <i class='fa-solid fa-award fs-3 text-body-secondary' />}
                            </div>
                            <div class='fw-semibold small'>{course.badgeTitle || course.title}</div>
                            <div class='small text-body-secondary'>
                                {progress
                                    ? (progress.lessonsCompletedDate
                                        ? (Number(course.exam) > 0
                                            ? <Text id='elearning.examNotPassed'>Exam not passed</Text>
                                            : <Text id='elearning.readAgain'>Read the lessons again to earn the badge</Text>)
                                        : <Text id='elearning.lessonsOf' fields={{ done, total }}>{done} of {total} lessons</Text>)
                                    : <Text id='elearning.notStarted'>Not started</Text>}
                            </div>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Profile tile wrapper: loads the caller's progress and the domain's courses itself.
 * Registered as ProfileElearningBadges in product/components-other.js.
 */
@observer
class ElearningBadges extends Component {
    componentDidMount() {
        const { elearningCourseStore, elearningProgressStore } = this.props.stores;
        elearningProgressStore.loadMine();
        elearningCourseStore.loadWithProgress();
    }

    render() {
        const { elearningCourseStore, elearningProgressStore } = this.props.stores;
        return (
            <ElearningBadgesView
                courses={elearningCourseStore.elearningCourses || []}
                progressRows={elearningProgressStore.elearningProgresses || []}
            />
        );
    }
}

export default ElearningBadges;
